import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { Cloud, CloudRain, Sun, CloudSnow, CloudLightning, Wind, Radio, User, Factory, Truck, Layers, Clock } from "lucide-react";

/* WMO weather codes → icon + label. Open-Meteo returns these. */
const wmo = (code) => {
  if (code == null) return { Icon: Cloud, label: "—" };
  if (code === 0) return { Icon: Sun, label: "Clear" };
  if ([1, 2, 3].includes(code)) return { Icon: Cloud, label: "Cloudy" };
  if ([45, 48].includes(code)) return { Icon: Cloud, label: "Fog" };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Rain" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Snow" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, label: "Storm" };
  return { Icon: Wind, label: "Wind" };
};

const shift = (d) => {
  const h = d.getHours();
  if (h >= 6 && h < 14) return "Day · 06–14";
  if (h >= 14 && h < 22) return "Afternoon · 14–22";
  return "Night · 22–06";
};

export default function LiveKpiBar({ activeTrucks = 0, productionM3 = 0, plantStatus = "idle" }) {
  const { user } = useAuth();
  const { plantId } = usePlant();
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/weather${plantId ? `?plant_id=${plantId}` : ""}`);
        if (!cancel) setWeather(r.data);
      } catch { /* silent — weather is decorative */ }
    })();
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/weather${plantId ? `?plant_id=${plantId}` : ""}`);
        if (!cancel) setWeather(r.data);
      } catch { /* ignore */ }
    }, 1000 * 60 * 15); // v3.11 · efficiency pass — refresh every 15 min (was 10)
    return () => { cancel = true; clearInterval(t); };
  }, [plantId]);

  const w = wmo(weather?.code);
  const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const good = plantStatus === "operational";

  const Cell = ({ Icon, label, value, tone }) => {
    const toneMap = {
      good: "text-emerald-500",
      warn: "text-amber-500",
      bad: "text-red-500",
      info: "text-sky-400",
      brand: "text-primary-orange",
    };
    return (
      <div className="flex items-center gap-2 min-w-0" data-testid={`kpi-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <Icon className={`w-4 h-4 shrink-0 ${toneMap[tone] || "text-primary-orange"}`} />
        <div className="min-w-0">
          <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial leading-tight">{label}</div>
          <div className="mono text-[13px] font-bold text-ink leading-tight truncate">{value}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="card-v2 px-4 py-3" data-testid="live-kpi-bar">
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-4 items-center">
        <Cell Icon={Clock} label="Now" value={time} tone="brand" />
        <Cell Icon={User} label="Dispatcher" value={user?.name || "—"} tone="info" />
        <Cell Icon={Radio} label="Plant" value={(plantStatus || "idle").toUpperCase()} tone={good ? "good" : "warn"} />
        <Cell Icon={w.Icon} label="Weather" value={weather?.temp_c != null ? `${w.label} · ${Math.round(weather.temp_c)}°C` : "—"} tone="info" />
        <Cell Icon={Sun} label="Temp" value={weather?.temp_c != null ? `${Math.round(weather.temp_c)}°C` : "—"} tone="warn" />
        <Cell Icon={Factory} label="Shift" value={shift(now)} tone="brand" />
        <Cell Icon={Truck} label="Active Trucks" value={activeTrucks} tone="good" />
        <Cell Icon={Layers} label="Today m³" value={`${productionM3.toFixed?.(1) ?? productionM3} m³`} tone="brand" />
      </div>
    </div>
  );
}
